import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useHubspotDeal, type HubspotSearchItem } from '@/features/assessments/api/hubspot-search'
import { HubspotPicker } from '@/features/assessments/containers/hubspot-picker'

export type HubspotDealFieldProps = {
  // The HubSpot deal ID stored on the installation phase.
  value: string | null
  onChange: (hubspotId: string | null) => void
  disabled?: boolean
  error?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  inline?: boolean
}

// The "HubSpot deal" field: the deals-only picker over a stored deal ID.
export function HubspotDealField({
  value,
  onChange,
  disabled,
  error,
  open,
  onOpenChange,
  inline
}: HubspotDealFieldProps) {
  const { t } = useTranslation('plantAssessment')
  // The last deal picked here; the ID alone cannot render the option.
  const [picked, setPicked] = useState<HubspotSearchItem | null>(null)

  const local = picked && picked.hubspotId === value ? picked : null
  const lookup = useHubspotDeal(local ? null : value)
  const deal = local ?? lookup.data ?? (value ? unresolvedDeal(value) : null)

  function handleChange(item: HubspotSearchItem | null) {
    setPicked(item)
    onChange(item?.hubspotId ?? null)
  }

  return (
    <HubspotPicker
      type="deal"
      value={deal}
      onChange={handleChange}
      disabled={disabled}
      error={error}
      open={open}
      onOpenChange={onOpenChange}
      inline={inline}
      label={t('installationPhaseForm.hubspotDealId.label')}
      placeholder={t('installationPhaseForm.hubspotDealId.placeholder')}
    />
  )
}

// A saved ID with no materialized deal (lookup pending, missing or failed):
// show the bare ID rather than an empty field that would hide the reference.
function unresolvedDeal(hubspotId: string): HubspotSearchItem {
  return {
    id: `deal:${hubspotId}`,
    type: 'deal',
    hubspotId,
    title: hubspotId,
    address: null,
    lastActivityAt: null,
    stageId: null,
    plantHubspotId: null,
    plantTitle: null,
    suggestedCountry: null,
    dealOwner: null,
    salesEngineerOwner: null
  }
}
